/**
 * #2903 — the words of the Access tab: sign-in, Telegram, added by, invite end, and the
 * filters. Pure functions, so the list, the drawer and the tests read the same lines.
 * The Access read has no last-active time and none shows.
 *
 * #2926 D6 — an instant (an invite sent, an invite end) reads as the day in the team's
 * time zone, `timeZone` below. A date with no time (joined, last day) reads as it is.
 */
import type { AccessInvite, AccessPerson } from "../../../api/useTeamAccess";
import { daysBetween, todayInZone } from "../../../floor/dates";

export type RoleChoice = "lead" | "co_lead" | "member";
export const ROLE_CHOICES: { value: RoleChoice; label: string }[] = [
  { value: "lead", label: "Lead" },
  { value: "co_lead", label: "Co-lead" },
  { value: "member", label: "Member" },
];

export function roleLabel(role: string): string {
  if (role === "co_lead") return "Co-lead";
  if (role === "lead") return "Lead";
  if (role === "member") return "Member";
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
}

const DAY = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const SHORT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const LONG = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/** The `YYYY-MM-DD` day of a date or an instant, in the team's zone. */
function zoneDay(iso: string, timeZone: string | undefined): string {
  return iso.length === 10 ? iso : todayInZone(timeZone, new Date(iso));
}

/** A day at noon UTC, for the UTC formatters above: the day never moves. */
function noon(day: string): Date {
  return new Date(`${day}T12:00:00Z`);
}

/** "Tue 20 Oct". */
export function dayWords(iso: string, timeZone?: string): string {
  return DAY.format(noon(zoneDay(iso, timeZone))).replace(",", "");
}

/** "20 Oct", or "3 Mar 2024" when the year is not this one. */
export function joinedWords(iso: string, nowMs = Date.now(), timeZone?: string): string {
  const day = zoneDay(iso, timeZone);
  const thisYear = todayInZone(timeZone, new Date(nowMs)).slice(0, 4);
  return day.slice(0, 4) === thisYear ? SHORT.format(noon(day)) : LONG.format(noon(day));
}

/** "tomorrow", "today", "in 5 days"; "ended" once past. */
export function endsIn(expiresAt: string, nowMs = Date.now(), timeZone?: string): string {
  const days = daysBetween(todayInZone(timeZone, new Date(nowMs)), zoneDay(expiresAt, timeZone));
  if (days < 0) return "ended";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

export function inviteOf(p: AccessPerson, invites: AccessInvite[]): AccessInvite | undefined {
  return invites.find((i) => i.member_id === p.membership_id);
}

export interface SignIn {
  main: string;
  sub: string | null;
  /** Amber: something waits on the person. */
  waiting: boolean;
}

export function signInWords(
  p: AccessPerson,
  invite: AccessInvite | undefined,
  timeZone?: string,
): SignIn {
  if (p.has_login) return { main: "Can sign in", sub: null, waiting: false };
  if (p.has_account) return { main: "Telegram only", sub: null, waiting: false };
  if (invite) {
    const end = invite.channel === "email" ? endsIn(invite.expires_at, Date.now(), timeZone) : null;
    return {
      main: "Not joined",
      sub: end ? `invite ${end === "ended" ? "ended" : `ends ${end}`}` : null,
      waiting: true,
    };
  }
  return { main: "Roster only", sub: null, waiting: false };
}

export function telegramWords(p: AccessPerson, invite: AccessInvite | undefined): string {
  if (!p.has_account && invite?.channel === "telegram") return "Link not used";
  switch (p.telegram) {
    case "connected":
      return "Linked";
    case "not_connected":
      return p.has_account ? "Not linked" : "—";
    case "failed":
      return "Failing";
    case "stopped":
      return "Stopped";
    default:
      return "—";
  }
}

/** How a seat came onto the team (`added_via`). */
export const VIA_WORDS: Record<string, string> = {
  email: "by email",
  telegram: "by Telegram link",
  join_link: "by join link",
  manual: "added by hand",
  import: "import",
  staff: "Tarii",
  team_create: "Started the team",
};

/** How an open invite went (`channel`). An invite row never reads "added by hand". */
export const CHANNEL_WORDS: Record<string, string> = {
  email: "by email",
  telegram: "by Telegram link",
};

export interface AddedBy {
  who: string;
  how: string | null;
  /** "29 Oct": the day the invite went, in the team's zone. */
  day: string | null;
  /** "Thu 29 Oct": the same day for the person drawer. */
  dayLong: string | null;
}

export function addedByWords(
  p: AccessPerson,
  invite: AccessInvite | undefined,
  timeZone?: string,
): AddedBy {
  const dayLong = invite ? dayWords(invite.created_at, timeZone) : null;
  const day = dayLong ? dayLong.replace(/^\w+ /, "") : null;
  if (p.added_via === "team_create") {
    return { who: "Started the team", how: null, day: null, dayLong: null };
  }
  const who = p.added_by_tarii ? "Tarii" : (invite?.invited_by_name ?? p.added_by_name ?? null);
  // #2926 D7 — an invite says how it went; `added_via` says how the seat came, which for
  // a seat added by hand and then invited is "manual".
  const via = invite
    ? (CHANNEL_WORDS[invite.channel] ?? null)
    : p.added_via
      ? (VIA_WORDS[p.added_via] ?? null)
      : null;
  if (!who) return { who: "—", how: via, day, dayLong };
  return { who, how: p.added_by_tarii ? null : via, day, dayLong };
}

export type AccessFilter = "all" | "not_joined" | "new" | "no_telegram";

export function isNewThisMonth(p: AccessPerson, nowMs = Date.now(), timeZone?: string): boolean {
  return (
    p.has_account && p.joined_at.slice(0, 7) === todayInZone(timeZone, new Date(nowMs)).slice(0, 7)
  );
}

export function matchesFilter(
  p: AccessPerson,
  f: AccessFilter,
  nowMs = Date.now(),
  timeZone?: string,
): boolean {
  switch (f) {
    case "not_joined":
      return p.group === "not_joined";
    case "new":
      return isNewThisMonth(p, nowMs, timeZone);
    case "no_telegram":
      return p.has_account && (p.telegram === null || p.telegram === "not_connected");
    default:
      return true;
  }
}

export function matchesQuery(
  p: AccessPerson,
  invite: AccessInvite | undefined,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return p.name.toLowerCase().includes(q) || (invite?.email ?? "").toLowerCase().includes(q);
}
